﻿import { logger, TagBorderMode } from '@fe/core';
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
    UDViewDayNightShadingType as ApiUDViewDayNightShadingType,
    UDViewFilterGroupModel as ApiUDViewFilterGroupModel,
    UDViewModel as ApiUDViewModel,
    UDViewPlaneFilterSettingModel as ApiUDViewPlaneFilterSettingModel,
    UdViewCreateOrUpdateRequestModel,
    UDViewGeojsonPathModel,
    ViewsService
} from '../../../services/axios/axios-api-v2-generated';
import { airportNavSettingsFromApi, airportNavSettingsToApi, DEFAULT_AIRPORT_NAV_SETTINGS } from '../../../udView/nav-settings-from-view';
import {
    UDViewCommonSettingsItem,
    UDViewDayNightShadingType,
    UDViewDayNightTwilightType,
    UDViewFilterGroupModel,
    UDViewGeojsonFilePath,
    UDViewGeojsonUrlPath,
    UDViewHistoryColorSource,
    UDViewMapModel,
    UDViewMapStyle,
    UdViewModel,
    UDViewPlaneFilterSettingModel,
    UDViewPlaneTagLabelModel,
    UDViewRangeType,
    UDViewTagSelectionEnum,
    UDViewTerrainShadingType,
    UDViewTimeDistanceMode,
    UDViewType
} from '../../../udView/udview';
import feVersion from '../../../utils/version-helper';
import { EventBus } from '../../contexts/event-bus';
import { addNotification, NotificationType } from './global-slice';

export function createDefaultView(): UdViewModel {
    return {
        name: 'Default',
        id: '',
        currentDbFEWebVersion: null,
        currentFEWebVersion: null,
        originalDbFEWebVersion: null,
        originalFEWebVersion: null,
        deleted: false,
        type: UDViewType.user,
        includeInSlideshow: false,
        isFavorite: false,
        lastUpdate: null,
        plane: {
            settings: {
                show: true,
                showHistory: false,
                showActive: true,
                showParked: true,
                showScheduled: false,
                showPlanned: false,
                showDiversionLines: false,
                showGroundVehicles: false,
                historyColor: '#f0f',
                parkedColor: null,
                scheduledColor: null,
                plannedColor: null,
                flightPlanColor: null,
                oldFlightPlanColor: null,
                routeLabelsColor: null,
                routeFlownColor: null,
                flown3PdiColor: null,
                flownProjectionColor: null,
                showFlightPlanPointMark: false,
                showFlightPlanPointLabel: false,
                flightPlanExtent: false,
                showRouteFlownPointMark: false,
                showFlightPlanTime: 0,
                showProjectedPoints: true,
                showLastReal: false,
                showProjectedHighlight: false,
                projectedHighlightColor: null,
                enableCoastMode: false,
                historyModern: false,
                historyColorSource: UDViewHistoryColorSource.history,
                showCoastPosition: false,
                coastPositionColor: null,
                enableTimeDistance: false,
                udViewTimeDistanceMode: UDViewTimeDistanceMode.lastNext,
                routeFlown: {
                    defaultColor: 'rgb(0,0,0)',
                    projected: { enabled: true, color: 'rgb(255,255,255)' },
                    acars: { enabled: true, color: 'rgb(71,212,90)' },
                    adsbTerrestrial: { enabled: true, color: 'rgb(33,94,153)' },
                    adsbSatellite: { enabled: true, color: 'rgb(77,148,216)' },
                    faaAnsp: { enabled: true, color: 'rgb(0,0,0)' }
                },
                cityPair: {
                    show: false,
                    active: true,
                    planned: false,
                    scheduled: false,
                    activeColor: '#5585f5',
                    plannedColor: '#00dc00',
                    scheduledColor: '#ebebc8'
                }
            },
            filter: {
                useFilters: false,
                startWithNoPlanes: false,
                planeFilterSettings: []
            },
            icon: {
                zoomLevel: 0,
                size: 0,
                type: 0,
                drawShadow: false
            },
            tag: {
                show: false,
                labels: [
                    {
                        type: UDViewTagSelectionEnum.Acid,
                        label: null,
                        enabled: true
                    },
                    {
                        type: UDViewTagSelectionEnum.None,
                        label: null,
                        enabled: true
                    },
                    {
                        type: UDViewTagSelectionEnum.Altitude,
                        label: null,
                        enabled: true
                    },
                    {
                        type: UDViewTagSelectionEnum.Speed,
                        label: null,
                        enabled: true
                    },
                    {
                        type: UDViewTagSelectionEnum.Origin,
                        label: null,
                        enabled: true
                    },
                    {
                        type: UDViewTagSelectionEnum.Destination,
                        label: null,
                        enabled: true
                    },
                    {
                        type: UDViewTagSelectionEnum.AircraftType,
                        label: null,
                        enabled: true
                    },
                    {
                        type: UDViewTagSelectionEnum.EtaFaa,
                        label: null,
                        enabled: true
                    },
                    {
                        type: UDViewTagSelectionEnum.None,
                        label: null,
                        enabled: false
                    },
                    {
                        type: UDViewTagSelectionEnum.None,
                        label: null,
                        enabled: false
                    },
                    {
                        type: UDViewTagSelectionEnum.None,
                        label: null,
                        enabled: false
                    },
                    {
                        type: UDViewTagSelectionEnum.None,
                        label: null,
                        enabled: false
                    }
                ],
                zoomLevel: 0,
                disableTagLeader: false,
                enableAutoTag: false,
                tagShowMyNotesIcon: false,
                hideAirlineId: false,
                showLabelBackground: true,
                backgroundColor: '#00000099',
                outlineColor: '#fff',
                outlineMode: TagBorderMode.none,
                textColor: '#fff'
            },
            range: {
                isVisible: false,
                factor: 30,
                type: UDViewRangeType.distance,
                color: '#12cafc40',
                lineColor: '#12cafc'
            },
            vector: {
                isVisible: false,
                factor: 25,
                type: UDViewRangeType.distance,
                lineColor: '#12cafc'
            }
        },
        weather: {
            show: true,
            hidden: [],
            all: [],
            settings: []
        },
        staticData: {
            airport: {
                settings: DEFAULT_AIRPORT_NAV_SETTINGS,
                show: false,
                myAirports: { show: false, color: null },
                tower: { show: true, color: null },
                noTower: { show: false, color: null },
                heliport: { show: false, color: null },
                seaplane: { show: false, color: null },
                others: { show: false, color: null },
                runways: true,
                deicing: false,
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            airspace: {
                show: false,
                fir: true,
                uir: true,
                classA: false,
                classB: true,
                classC: true,
                classD: false,
                classE: false,
                classF: false,
                classG: false,
                other: false,
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            airway: {
                show: false,
                high: { show: true, color: null },
                low: { show: false, color: null },
                settings: {
                    fontSize: 10
                },
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            drawingObjects: {
                show: false,
                avoidance: true,
                alert: true,
                view: true,
                viewObjectIds: []
            },
            fixes: {
                show: false,
                latLon: { show: false, color: null },
                nrs: { show: false, color: null },
                intersection: { show: false, color: null },
                rnav: { show: true, color: null },
                terminal: { show: false, color: null },
                vfr: { show: false, color: null },
                other: { show: false, color: null },
                aacaA_SUA: { show: false, color: null },
                low: true,
                high: true,
                settings: {
                    fontSize: 9
                },
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            flightServiceStation: {
                show: false,
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            geographyMap: {
                drawDayNight: false
            },
            holding: {
                show: false,
                lowAltitude: true,
                highAltitude: true,
                sid: true,
                star: true,
                approach: false,
                missedApproach: true,
                other: false,
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            gridMora: { show: false },
            latLon: {
                show: false
            },
            navaids: {
                show: false,
                ndb: { show: true, color: null },
                vor: { show: true, color: null },
                voR_DME: { show: true, color: null },
                vortac: { show: true, color: null },
                tacan: { show: true, color: null },
                dme: { show: false, color: null },
                ndB_DME: { show: true, color: null },
                rings: {
                    draw: false,
                    drawTerminal: false,
                    drawLowAltitude: false,
                    drawHighAltitude: false,
                    drawUnrestricted: false,
                    fillColor: 'rgba(0,0,255,0.2)',
                    lineColor: 'rgba(0,0,255,0.5)'
                },
                settings: {
                    fontSize: 9
                },
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            overwaterAreas: {
                show: false,
                lineColor: '#4169e1b3'
            },
            miscellaneous: {
                backGroundBrightness: 255
            },
            sids: {
                show: false,
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            stars: {
                show: false,
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            sua: {
                show: false,
                static: true,
                dynamic: false,
                alert: true,
                caution: true,
                danger: true,
                militaryArea: true,
                prohibited: true,
                restricted: true,
                traning: true,
                warning: true,
                unkown: true,
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            timeZone: { show: false },
            weatherStation: {
                show: false,
                filterGroup: {
                    show: false,
                    ids: [],
                    config: {}
                }
            },
            customGeojson: {
                show: false,
                paths: []
            }
        },
        map: {
            style: UDViewMapStyle.light,
            pitch: 0,
            bearing: 0,
            lat: 0,
            lon: 0,
            zoom: 2,
            dayNightShading: false,
            dayNightShadingType: UDViewDayNightShadingType.shade,
            selectedTwilightTypes: UDViewDayNightTwilightType.sunrise | UDViewDayNightTwilightType.civil,
            terrain: false,
            useGlobeView: false,
            globeAutoRotate: false,
            globeAutoRotateSpeed: 5,
            terrainShading: {
                enabled: false,
                type: UDViewTerrainShadingType.singleColor,
                altitude: 10000,
                opacity: 50,
                color: 'rgba(255,0,0,0.3)'
            }
        }
    };
}

const initialState = {
    busy: false,
    items: [] as UdViewModel[]
};

export const viewsSlice = createSlice({
    name: 'views',
    initialState: initialState,
    reducers: {
        setViews: (state, params: { payload: { views: ApiUDViewModel[] } }) => {
            const newViews = params.payload.views.map(viewFromApi);
            state.items = [...newViews.filter((x) => x.isFavorite), createDefaultView(), ...newViews.filter((x) => !x.isFavorite)];
            state.items = applySort(state.items);
        },
        processViewUpdates: (state, params: { payload: { views: ApiUDViewModel[]; deleted: string[] } }) => {
            for (const view of params.payload.views) {
                if (view.deleted) {
                    state.items = state.items.filter((x) => x.id !== view.id);
                } else {
                    const idx = state.items.findIndex((x) => x.id === view.id);
                    if (idx > -1) {
                        state.items[idx] = viewFromApi(view);
                    } else {
                        state.items.push(viewFromApi(view));
                    }
                }
            }
            for (const deleted of params.payload.deleted) {
                state.items = state.items.filter((x) => x.id !== deleted);
            }
            state.items = applySort(state.items);
        }
    },
    extraReducers: (builder) => {
        builder.addCase(importViews.fulfilled, (state, action) => {
            if (action.payload.successImports.length > 0) {
                state.items.push(...action.payload.successImports);
                state.items = applySort(state.items);
            }
        });

        builder.addCase(createView.fulfilled, (state, action) => {
            state.items.push(action.payload);
            state.items = applySort(state.items);
        });

        builder.addCase(updateView.fulfilled, (state, action) => {
            const index = state.items.findIndex((v) => v.id === action.payload.id);
            if (index !== -1) {
                state.items[index] = { ...action.payload };
                state.items = applySort(state.items);
            }
        });

        builder.addCase(deleteView.fulfilled, (state, action) => {
            state.items = state.items.filter((x) => x.id !== action.meta.arg.view.id);
        });

        builder.addMatcher(isAnyOf(createView.fulfilled, updateView.fulfilled, deleteView.fulfilled), (state) => {
            state.busy = true;
        });

        builder.addMatcher(
            isAnyOf(
                updateView.fulfilled,
                updateView.rejected,
                createView.fulfilled,
                createView.rejected,
                deleteView.fulfilled,
                deleteView.rejected
            ),
            (state) => {
                state.busy = false;
            }
        );
    }
});

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype;
}

// Fills fields missing in `source` (e.g. views exported from older versions) from `defaults`.
function mergeWithDefaults<T>(defaults: T, source: unknown): T {
    if (source === undefined || source === null) {
        return defaults;
    }
    if (!isPlainObject(defaults) || !isPlainObject(source)) {
        return source as T;
    }
    const result: Record<string, unknown> = { ...defaults };
    for (const key of Object.keys(source)) {
        result[key] = mergeWithDefaults(defaults[key], source[key]);
    }
    return result as T;
}

export const importViews = createAsyncThunk('views/import', async (params: { views: UdViewModel[] }, api) => {
    const failedImports: UdViewModel[] = [];
    const successfulImports: UdViewModel[] = [];
    for (const view of params.views) {
        try {
            const newView: UdViewModel = {
                ...mergeWithDefaults(createDefaultView(), view),
                isFavorite: false,
                type: UDViewType.user,
                currentDbFEWebVersion: feVersion.databaseVersion,
                currentFEWebVersion: feVersion.version,
                originalDbFEWebVersion: feVersion.databaseVersion,
                originalFEWebVersion: feVersion.version
            };
            const response = await ViewsService.createAsync(
                {
                    body: viewToApi(newView)
                },
                { withAuthorization: true }
            );
            successfulImports.push({
                ...newView,
                id: response.id!
            });
        } catch (error) {
            logger.error('Failed to import view', view.name, error);
            failedImports.push(view);
        }
    }

    api.dispatch(
        addNotification({
            type: NotificationType.udViewsImportSuccess,
            customMessage: `Imported: ${successfulImports.length} view(s)`
        })
    );

    if (failedImports.length > 0) {
        api.dispatch(
            addNotification({
                type: NotificationType.udViewsImportFailed,
                customMessage: `Failed to import views: ${failedImports.map((x) => x.name).join(', ')}`
            })
        );
    }

    return api.fulfillWithValue({
        failedImports,
        successImports: successfulImports
    });
});

export const createView = createAsyncThunk(
    'views/create',
    async (
        params: {
            workspaceId: number;
            view: UdViewModel;
        },
        api
    ) => {
        try {
            let state: Pick<UDViewMapModel, 'lat' | 'lon' | 'zoom' | 'pitch' | 'bearing'> | undefined;

            // ask for map state
            EventBus.fire('get-map-state', {
                workspaceId: params.workspaceId,
                viewId: params.view.id,
                result: (s) => (state = s)
            });

            const view: UdViewModel = {
                ...params.view,
                currentDbFEWebVersion: feVersion.databaseVersion,
                currentFEWebVersion: feVersion.version,
                originalDbFEWebVersion: feVersion.databaseVersion,
                originalFEWebVersion: feVersion.version
            };

            // fill state if any
            if (state) {
                view.map = {
                    ...view.map,
                    ...state
                };
            }

            const response = await ViewsService.createAsync(
                {
                    body: viewToApi({
                        ...params.view,
                        isFavorite: false
                    })
                },
                { withAuthorization: true }
            );
            return api.fulfillWithValue({
                ...params.view,
                id: response.id!
            });
        } catch {
            api.dispatch(addNotification({ type: NotificationType.udViewSaveError }));
            return api.rejectWithValue(undefined);
        }
    }
);

export const updateView = createAsyncThunk(
    'views/update',
    async (
        params: {
            workspaceId: number;
            view: UdViewModel;
        },
        api
    ) => {
        let state: Pick<UDViewMapModel, 'lat' | 'lon' | 'zoom' | 'pitch' | 'bearing'> | undefined;

        // ask for map state
        EventBus.fire('get-map-state', {
            workspaceId: params.workspaceId,
            viewId: params.view.id,
            result: (s) => (state = s)
        });

        const view: UdViewModel = {
            ...params.view,
            currentDbFEWebVersion: feVersion.databaseVersion,
            currentFEWebVersion: feVersion.version,
            originalDbFEWebVersion: params.view.originalDbFEWebVersion ?? feVersion.databaseVersion,
            originalFEWebVersion: params.view.originalFEWebVersion ?? feVersion.version
        };

        // fill state if any
        if (state) {
            view.map = {
                ...view.map,
                ...state
            };
        }

        try {
            await ViewsService.updateAsync({ id: view.id, body: viewToApi(view) }, { withAuthorization: true });
        } catch {
            api.dispatch(addNotification({ type: NotificationType.udViewSaveError }));
            return api.rejectWithValue(undefined);
        }

        return api.fulfillWithValue(view);
    }
);

export const deleteView = createAsyncThunk(
    'views/delete',
    async (
        params: {
            workspaceId: number;
            view: UdViewModel;
        },
        api
    ) => {
        try {
            await ViewsService.deleteAsync({ id: params.view.id }, { withAuthorization: true });
        } catch {
            api.dispatch(addNotification({ type: NotificationType.udViewSaveError }));
            return api.rejectWithValue(undefined);
        }
        return api.fulfillWithValue(undefined);
    }
);

export const { setViews, processViewUpdates } = viewsSlice.actions;

function applySort(view: UdViewModel[]): UdViewModel[] {
    return [
        ...view.filter((x) => x.isFavorite && x.id).sort((a, b) => a.name.localeCompare(b.name)),
        ...view.filter((x) => !x.id),
        ...view.filter((x) => !x.isFavorite && x.id).sort((a, b) => a.name.localeCompare(b.name))
    ];
}

function viewFromApi(view: ApiUDViewModel): UdViewModel {
    // TODO: here if some property is null we default it to null or false. What we should be doing is taking defaults from default ud view instead.
    return {
        id: view.id ?? '',
        currentDbFEWebVersion: view.currentDbFEWebVersion ?? null,
        currentFEWebVersion: view.currentFEWebVersion ?? null,
        originalDbFEWebVersion: view.originalDbFEWebVersion ?? null,
        originalFEWebVersion: view.originalFEWebVersion ?? null,
        name: view.name ?? '',
        lastUpdate: view.lastUpdate ?? null,
        deleted: view.deleted ?? false,
        type: view.type ?? UDViewType.user,
        includeInSlideshow: view.includeInSlideshow ?? false,
        isFavorite: view.isFavorite ?? false,
        plane: {
            settings: {
                show: view.plane?.settings?.show ?? false,
                showHistory: view.plane?.settings?.showHistory ?? false,
                showActive: view.plane?.settings?.showActive ?? false,
                showParked: view.plane?.settings?.showParked ?? false,
                showScheduled: view.plane?.settings?.showScheduled ?? false,
                showPlanned: view.plane?.settings?.showPlanned ?? false,
                showDiversionLines: view.plane?.settings?.showDiversionLines ?? false,
                showGroundVehicles: view.plane?.settings?.showGroundVehicles ?? false,
                historyColor: view.plane?.settings?.historyColor ?? null,
                parkedColor: view.plane?.settings?.parkedColor ?? null,
                scheduledColor: view.plane?.settings?.scheduledColor ?? null,
                plannedColor: view.plane?.settings?.plannedColor ?? null,
                flightPlanColor: view.plane?.settings?.flightPlanColor ?? null,
                oldFlightPlanColor: view.plane?.settings?.oldFlightPlanColor ?? null,
                routeLabelsColor: view.plane?.settings?.routeLabelsColor ?? null,
                routeFlownColor: view.plane?.settings?.routeFlownColor ?? null,
                flown3PdiColor: view.plane?.settings?.flown3PdiColor ?? null,
                flownProjectionColor: view.plane?.settings?.flownProjectionColor ?? null,
                showFlightPlanPointMark: view.plane?.settings?.showFlightPlanPointMark ?? false,
                showFlightPlanPointLabel: view.plane?.settings?.showFlightPlanPointLabel ?? false,
                flightPlanExtent: view.plane?.settings?.flightPlanExtent ?? false,
                showRouteFlownPointMark: view.plane?.settings?.showRouteFlownPointMark ?? false,
                showFlightPlanTime: view.plane?.settings?.showFlightPlanTime ?? 0,
                showProjectedPoints: view.plane?.settings?.showProjectedPoints ?? false,
                showLastReal: view.plane?.settings?.showLastReal ?? false,
                showProjectedHighlight: view.plane?.settings?.showProjectedHighlight ?? false,
                projectedHighlightColor: view.plane?.settings?.projectedHighlightColor ?? null,
                enableCoastMode: view.plane?.settings?.enableCoastMode ?? false,
                historyModern: view.plane?.settings?.historyModern ?? false,
                historyColorSource:
                    view.plane?.settings?.historyColorSource === 0 ? UDViewHistoryColorSource.aircraft : UDViewHistoryColorSource.history,
                showCoastPosition: view.plane?.settings?.showCoastPosition ?? false,
                coastPositionColor: view.plane?.settings?.coastPositionColor ?? null,
                enableTimeDistance: view.plane?.settings?.enableTimeDistance ?? false,
                udViewTimeDistanceMode: view.plane?.settings?.udViewTimeDistanceMode ?? 0,
                routeFlown: {
                    defaultColor: view.plane?.settings?.routeFlown?.defaultColor ?? 'rgb(0,0,0)',
                    projected: {
                        enabled: view.plane?.settings?.routeFlown?.projected?.enabled ?? true,
                        color: view.plane?.settings?.routeFlown?.projected?.color ?? 'rgb(255,255,255)'
                    },
                    acars: {
                        enabled: view.plane?.settings?.routeFlown?.acars?.enabled ?? true,
                        color: view.plane?.settings?.routeFlown?.acars?.color ?? 'rgb(71,212,90)'
                    },
                    adsbTerrestrial: {
                        enabled: view.plane?.settings?.routeFlown?.adsbTerrestrial?.enabled ?? true,
                        color: view.plane?.settings?.routeFlown?.adsbTerrestrial?.color ?? 'rgb(33,94,153)'
                    },
                    adsbSatellite: {
                        enabled: view.plane?.settings?.routeFlown?.adsbSatellite?.enabled ?? true,
                        color: view.plane?.settings?.routeFlown?.adsbSatellite?.color ?? 'rgb(77,148,216)'
                    },
                    faaAnsp: {
                        enabled: view.plane?.settings?.routeFlown?.faaAnsp?.enabled ?? true,
                        color: view.plane?.settings?.routeFlown?.faaAnsp?.color ?? 'rgb(0,0,0)'
                    }
                },
                cityPair: {
                    show: view.plane?.settings?.cityPair?.show ?? false,
                    active: view.plane?.settings?.cityPair?.active ?? true,
                    planned: view.plane?.settings?.cityPair?.planned ?? false,
                    scheduled: view.plane?.settings?.cityPair?.scheduled ?? false,
                    activeColor: view.plane?.settings?.cityPair?.activeColor ?? '#5585f5',
                    plannedColor: view.plane?.settings?.cityPair?.plannedColor ?? '#00dc00',
                    scheduledColor: view.plane?.settings?.cityPair?.scheduledColor ?? '#ebebc8'
                }
            },
            filter: {
                useFilters: view.plane?.filter?.useFilters ?? false,
                startWithNoPlanes: view.plane?.filter?.startWithNoPlanes ?? false,
                planeFilterSettings:
                    view.plane?.filter?.planeFilterSettings?.map(
                        (filter) =>
                            ({
                                name: filter.name ?? '',
                                color: filter.color ?? 'blue',
                                isActive: filter.isActive ?? false,
                                include: filter.include ?? false,
                                airlineType: filter.airlineType ?? 1,
                                useMyFleet: filter.useMyFleet ?? false,
                                acid: filter.acid ?? '',
                                origin: filter.origin ?? null,
                                destination: filter.destination ?? null,
                                destinationType: filter.destinationType ?? 0,
                                useAltAirport: filter.useAltAirport ?? false,
                                beaconCode: filter.beaconCode ?? null,
                                icaoCode: filter.icaoCode ?? null,
                                altitudeLower: filter.altitudeLower ?? null,
                                altitudeUpper: filter.altitudeUpper ?? null,
                                speedLower: filter.speedLower ?? null,
                                speedUpper: filter.speedUpper ?? null,
                                aircraftType: filter.aircraftType ?? null,
                                filterObjectType: filter.filterObjectType ?? 0,
                                timeBefore: filter.timeBefore ?? null,
                                timeAfter: filter.timeAfter ?? null,
                                timeType: filter.timeType ?? null,
                                distanceToDestination: filter.distanceToDestination ?? null,
                                sectorDeskId: filter.sectorDeskId ?? null,
                                tail: filter.tail ?? null,
                                diverted: filter.diverted ?? false,
                                holding: filter.holding ?? false,
                                showTags: filter.showTags ?? false,
                                showHistory: filter.showHistory ?? false,
                                showHolding: filter.holding ?? false,
                                showDiverting: filter.diverted ?? false,
                                tagTextColor: filter.tagTextColor ?? 0
                            }) satisfies UDViewPlaneFilterSettingModel
                    ) ?? []
            },
            icon: {
                zoomLevel: view.plane?.icon?.zoomLevel ?? 0,
                size: view.plane?.icon?.size ?? 0,
                type: view.plane?.icon?.type ?? 0,
                drawShadow: view.plane?.icon?.drawShadow ?? false
            },
            tag: {
                show: view.plane?.tag?.show ?? false,
                labels: (() => {
                    //this ensures we have 12 labels, since this is what blazor did and what is saved in db.
                    const defaultLabels = createDefaultView().plane.tag.labels;
                    const apiLabels = view.plane?.tag?.labels ?? [];
                    return defaultLabels.map((defaultLabel, index) => {
                        const apiLabel = apiLabels[index];
                        if (apiLabel) {
                            return {
                                type: apiLabel.type ?? UDViewTagSelectionEnum.None,
                                label: apiLabel.label ?? null,
                                enabled: apiLabel.enabled ?? false
                            } satisfies UDViewPlaneTagLabelModel;
                        }
                        return { ...defaultLabel };
                    });
                })(),
                zoomLevel: view.plane?.tag?.zoomLevel ?? 0,
                disableTagLeader: view.plane?.tag?.disableTagLeader ?? false,
                enableAutoTag: view.plane?.tag?.enableAutoTag ?? false,
                tagShowMyNotesIcon: view.plane?.tag?.tagShowMyNotesIcon ?? false,
                hideAirlineId: view.plane?.tag?.hideAirlineId ?? false,
                showLabelBackground: view.plane?.tag?.showLabelBackground ?? false,
                backgroundColor: view.plane?.tag?.backgroundColor ?? null,
                textColor: view.plane?.tag?.textColor ?? null,
                outlineColor: view.plane?.tag?.tagOutlineColor ?? null,
                outlineMode: view.plane?.tag?.tagOutlineMode ?? TagBorderMode.none
            },
            range: {
                isVisible: view.plane?.range?.isVisible ?? false,
                factor: view.plane?.range?.factor ?? 0,
                type: view.plane?.range?.type ?? 0,
                color: view.plane?.range?.color ?? null,
                lineColor: view.plane?.range?.lineColor ?? null
            },
            vector: {
                isVisible: view.plane?.vector?.isVisible ?? false,
                factor: view.plane?.vector?.factor ?? 0,
                type: view.plane?.vector?.type ?? 0,
                lineColor: view.plane?.vector?.lineColor ?? null
            }
        },
        weather: {
            show: view.weather?.show ?? false,
            all: view.weather?.WeatherList ?? [],
            hidden: view.weather?.hidden ?? [],
            settings:
                view.weather?.settings?.map(
                    (item) =>
                        ({
                            key: item.key ?? '',
                            value: item.value ?? ''
                        }) satisfies UDViewCommonSettingsItem
                ) ?? []
        },
        staticData: {
            airport: {
                show: view.staticData?.airport?.show ?? false,
                myAirports: {
                    show: view.staticData?.airport?.myAirports ?? false,
                    color: view.staticData?.airport?.myAirportsColor ?? null
                },
                tower: {
                    show: view.staticData?.airport?.tower ?? false,
                    color: view.staticData?.airport?.towerColor ?? null
                },
                noTower: {
                    show: view.staticData?.airport?.noTower ?? false,
                    color: view.staticData?.airport?.noTowerColor ?? null
                },
                heliport: {
                    show: view.staticData?.airport?.heliport ?? false,
                    color: view.staticData?.airport?.heliportColor ?? null
                },
                seaplane: {
                    show: view.staticData?.airport?.seaplane ?? false,
                    color: view.staticData?.airport?.seaplaneColor ?? null
                },
                others: {
                    show: view.staticData?.airport?.others ?? false,
                    color: view.staticData?.airport?.othersColor ?? null
                },
                runways: view.staticData?.airport?.runways ?? false,
                deicing: view.staticData?.airport?.deicing ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.airport?.filterGroup),
                settings: airportNavSettingsFromApi(view)
            },
            airspace: {
                show: view.staticData?.airspace?.show ?? false,
                fir: view.staticData?.airspace?.fir ?? false,
                uir: view.staticData?.airspace?.uir ?? false,
                classA: view.staticData?.airspace?.classA ?? false,
                classB: view.staticData?.airspace?.classB ?? false,
                classC: view.staticData?.airspace?.classC ?? false,
                classD: view.staticData?.airspace?.classD ?? false,
                classE: view.staticData?.airspace?.classE ?? false,
                classF: view.staticData?.airspace?.classF ?? false,
                classG: view.staticData?.airspace?.classG ?? false,
                other: view.staticData?.airspace?.other ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.airspace?.filterGroup)
            },
            airway: {
                show: view.staticData?.airway?.show ?? false,
                high: { show: view.staticData?.airway?.high ?? false, color: view.staticData?.airway?.highColor ?? null },
                low: { show: view.staticData?.airway?.low ?? false, color: view.staticData?.airway?.lowColor ?? null },
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.airway?.filterGroup),
                settings: {
                    fontSize: view.staticData?.airway?.settings?.fontSize ?? 10
                }
            },
            drawingObjects: {
                show: view.staticData?.drawingObjects?.show ?? false,
                avoidance: view.staticData?.drawingObjects?.avoidance ?? false,
                alert: view.staticData?.drawingObjects?.alert ?? false,
                view: view.staticData?.drawingObjects?.view ?? false,
                viewObjectIds: view.staticData?.drawingObjects?.viewObjectIds ?? []
            },
            fixes: {
                show: view.staticData?.fixes?.show ?? false,
                latLon: {
                    show: view.staticData?.fixes?.latLon ?? false,
                    color: view.staticData?.fixes?.latLonColor ?? null
                },
                nrs: { show: view.staticData?.fixes?.nrs ?? false, color: view.staticData?.fixes?.nrsColor ?? null },
                intersection: {
                    show: view.staticData?.fixes?.intersection ?? false,
                    color: view.staticData?.fixes?.intersectionColor ?? null
                },
                rnav: { show: view.staticData?.fixes?.rnav ?? false, color: view.staticData?.fixes?.rnavColor ?? null },
                terminal: {
                    show: view.staticData?.fixes?.terminal ?? false,
                    color: view.staticData?.fixes?.terminalColor ?? null
                },
                vfr: { show: view.staticData?.fixes?.vfr ?? false, color: view.staticData?.fixes?.vfrColor ?? null },
                other: {
                    show: view.staticData?.fixes?.other ?? false,
                    color: view.staticData?.fixes?.otherColor ?? null
                },
                aacaA_SUA: {
                    show: view.staticData?.fixes?.aacaA_SUA ?? false,
                    color: view.staticData?.fixes?.aacaaSuaColor ?? null
                },
                low: view.staticData?.fixes?.low ?? false,
                high: view.staticData?.fixes?.high ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.fixes?.filterGroup),
                settings: {
                    fontSize: view.staticData?.fixes?.settings?.fontSize ?? 9
                }
            },
            flightServiceStation: {
                show: view.staticData?.flightServiceStation?.show ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.flightServiceStation?.filterGroup)
            },
            geographyMap: {
                drawDayNight: view.staticData?.geographyMap?.drawDayNight ?? false
            },
            holding: {
                show: view.staticData?.holding?.show ?? false,
                lowAltitude: view.staticData?.holding?.lowAltitude ?? false,
                highAltitude: view.staticData?.holding?.highAltitude ?? false,
                sid: view.staticData?.holding?.sid ?? false,
                star: view.staticData?.holding?.star ?? false,
                approach: view.staticData?.holding?.approach ?? false,
                missedApproach: view.staticData?.holding?.missedApproach ?? false,
                other: view.staticData?.holding?.other ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.holding?.filterGroup)
            },
            gridMora: {
                show: view.staticData?.gridMora?.show ?? false
            },
            latLon: {
                show: view.staticData?.latLon?.show ?? false
            },
            overwaterAreas: {
                show: view.staticData?.overwaterAreas?.show ?? false,
                lineColor: view.staticData?.overwaterAreas?.lineColor ?? '#4169e1b3'
            },
            navaids: {
                show: view.staticData?.navaids?.show ?? false,
                ndb: { show: view.staticData?.navaids?.ndb ?? false, color: view.staticData?.navaids?.ndbColor ?? null },
                vor: { show: view.staticData?.navaids?.vor ?? false, color: view.staticData?.navaids?.vorColor ?? null },
                voR_DME: {
                    show: view.staticData?.navaids?.voR_DME ?? false,
                    color: view.staticData?.navaids?.vorDmeColor ?? null
                },
                vortac: {
                    show: view.staticData?.navaids?.vortac ?? false,
                    color: view.staticData?.navaids?.vortacColor ?? null
                },
                tacan: {
                    show: view.staticData?.navaids?.tacan ?? false,
                    color: view.staticData?.navaids?.tacanColor ?? null
                },
                dme: { show: view.staticData?.navaids?.dme ?? false, color: view.staticData?.navaids?.dmeColor ?? null },
                ndB_DME: {
                    show: view.staticData?.navaids?.ndB_DME ?? false,
                    color: view.staticData?.navaids?.ndbDmeColor ?? null
                },
                rings: {
                    draw: view.staticData?.navaids?.rings?.draw ?? false,
                    drawTerminal: view.staticData?.navaids?.rings?.drawTerminal ?? false,
                    drawLowAltitude: view.staticData?.navaids?.rings?.drawLow ?? false,
                    drawHighAltitude: view.staticData?.navaids?.rings?.drawHigh ?? false,
                    drawUnrestricted: view.staticData?.navaids?.rings?.drawUnrestricted ?? false,
                    fillColor: view.staticData?.navaids?.rings?.fillColor ?? 'rgba(0,0,255,0.2)',
                    lineColor: view.staticData?.navaids?.rings?.lineColor ?? 'rgba(0,0,255,0.5)'
                },
                settings: {
                    fontSize: view.staticData?.navaids?.settings?.fontSize ?? 9
                },
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.navaids?.filterGroup)
            },
            miscellaneous: {
                backGroundBrightness: view.staticData?.miscellaneous?.backGroundBrightness ?? 255
            },
            sids: {
                show: view.staticData?.sids?.show ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.sids?.filterGroup)
            },
            stars: {
                show: view.staticData?.stars?.show ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.stars?.filterGroup)
            },
            sua: {
                show: view.staticData?.sua?.show ?? false,
                static: view.staticData?.sua?.static ?? false,
                dynamic: view.staticData?.sua?.dynamic ?? false,
                alert: view.staticData?.sua?.alert ?? false,
                caution: view.staticData?.sua?.caution ?? false,
                danger: view.staticData?.sua?.danger ?? false,
                militaryArea: view.staticData?.sua?.militaryArea ?? false,
                prohibited: view.staticData?.sua?.prohibited ?? false,
                restricted: view.staticData?.sua?.restricted ?? false,
                traning: view.staticData?.sua?.traning ?? false,
                warning: view.staticData?.sua?.warning ?? false,
                unkown: view.staticData?.sua?.unkown ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.sua?.filterGroup)
            },
            timeZone: {
                show: view.staticData?.timeZone?.show ?? false
            },
            weatherStation: {
                show: view.staticData?.weatherStation?.show ?? false,
                filterGroup: apiFilterGroupToFilterGroup(view.staticData?.weatherStation?.filterGroup)
            },
            customGeojson: {
                show: view.staticData?.customGeojson?.show ?? false,
                paths:
                    view.staticData?.customGeojson?.paths?.map((path, index) => {
                        if (path.type === 0) {
                            return {
                                type: 'url' as const,
                                name: path.name ?? `custom-${index}`,
                                show: path.show ?? false,
                                url: path.url ?? ''
                            } satisfies UDViewGeojsonUrlPath;
                        } else {
                            return {
                                type: 'file' as const,
                                name: path.name ?? `custom-${index}`,
                                show: path.show ?? false,
                                fileHandle: path.fileHandle ?? ''
                            } satisfies UDViewGeojsonFilePath;
                        }
                    }) ?? []
            }
        },
        map: {
            dayNightShading: view.map?.dayNightShading ?? false,
            dayNightShadingType: shadingTypeToWeb(view.map?.dayNightShadingType ?? 0),
            selectedTwilightTypes: view.map?.selectedTwilightTypes ?? UDViewDayNightTwilightType.none,
            lat: view.map?.lat ?? 0,
            lon: view.map?.lon ?? 0,
            pitch: view.map?.pitch ?? 0,
            bearing: view.map?.bearing ?? 0,
            zoom: view.map?.zoom ?? 0,
            terrain: view.map?.terrain ?? false,
            style: view.map?.style ?? UDViewMapStyle.dark,
            useGlobeView: view.map?.useGlobeView ?? false,
            globeAutoRotate: view.map?.globeAutoRotate ?? false,
            globeAutoRotateSpeed: view.map?.globeAutoRotateSpeed ?? 5,
            terrainShading: {
                enabled: view.map?.terrainShading?.enabled ?? false,
                opacity: view.map?.terrainShading?.opacity ?? 50,
                type: view.map?.terrainShading?.type ?? UDViewTerrainShadingType.singleColor,
                altitude: view.map?.terrainShading?.altitude ?? 10000,
                color: view.map?.terrainShading?.color ?? 'rgba(255,0,0,0.3)'
            }
        }
    };
}

function shadingTypeToWeb(input: ApiUDViewDayNightShadingType): UDViewDayNightShadingType {
    switch (input) {
        case 0:
        case 1:
            return UDViewDayNightShadingType.shade;
        case 2:
            return UDViewDayNightShadingType.twilight;
    }
}

export function filterToApi(filter: UDViewPlaneFilterSettingModel): ApiUDViewPlaneFilterSettingModel {
    return {
        name: filter.name,
        color: filter.color,
        isActive: filter.isActive,
        include: filter.include,
        airlineType: filter.airlineType,
        useMyFleet: filter.useMyFleet,
        acid: filter.acid ?? undefined,
        origin: filter.origin ?? undefined,
        destination: filter.destination ?? undefined,
        destinationType: filter.destinationType,
        useAltAirport: filter.useAltAirport,
        beaconCode: filter.beaconCode ?? undefined,
        icaoCode: filter.icaoCode ?? undefined,
        altitudeLower: filter.altitudeLower ?? undefined,
        altitudeUpper: filter.altitudeUpper ?? undefined,
        speedLower: filter.speedLower ?? undefined,
        speedUpper: filter.speedUpper ?? undefined,
        aircraftType: filter.aircraftType ?? undefined,
        filterObjectType: filter.filterObjectType,
        timeBefore: filter.timeBefore ?? undefined,
        timeAfter: filter.timeAfter ?? undefined,
        timeType: filter.timeType ?? undefined,
        distanceToDestination: filter.distanceToDestination ?? undefined,
        sectorDeskId: filter.sectorDeskId ?? undefined,
        tail: filter.tail ?? undefined,
        diverted: filter.diverted,
        holding: filter.holding,
        showTags: filter.showTags,
        showHistory: filter.showHistory,
        tagTextColor: filter.tagTextColor
    } satisfies ApiUDViewPlaneFilterSettingModel;
}

function viewToApi(view: UdViewModel): UdViewCreateOrUpdateRequestModel {
    return {
        name: view.name,
        currentDbFEWebVersion: view.currentDbFEWebVersion ?? undefined,
        currentFEWebVersion: view.currentFEWebVersion ?? undefined,
        originalDbFEWebVersion: view.originalDbFEWebVersion ?? undefined,
        originalFEWebVersion: view.originalFEWebVersion ?? undefined,
        type: view.type,
        includeInSlideshow: view.includeInSlideshow,
        isFavorite: view.isFavorite,
        plane: {
            settings: {
                show: view.plane.settings.show,
                showHistory: view.plane.settings.showHistory,
                showActive: view.plane.settings.showActive,
                showParked: view.plane.settings.showParked,
                showScheduled: view.plane.settings.showScheduled,
                showPlanned: view.plane.settings.showPlanned,
                showDiversionLines: view.plane.settings.showDiversionLines,
                showGroundVehicles: view.plane.settings.showGroundVehicles,
                historyColor: view.plane.settings.historyColor ?? undefined,
                parkedColor: view.plane.settings.parkedColor ?? undefined,
                scheduledColor: view.plane.settings.scheduledColor ?? undefined,
                plannedColor: view.plane.settings.plannedColor ?? undefined,
                flightPlanColor: view.plane.settings.flightPlanColor ?? undefined,
                oldFlightPlanColor: view.plane.settings.oldFlightPlanColor ?? undefined,
                routeLabelsColor: view.plane.settings.routeLabelsColor ?? undefined,
                routeFlownColor: view.plane.settings.routeFlownColor ?? undefined,
                // prettier-ignore
                routeFlown: {
                    defaultColor: view.plane.settings.routeFlown.defaultColor,
                    projected: {
                        enabled: view.plane.settings.routeFlown.projected.enabled,
                        color: view.plane.settings.routeFlown.projected.color
                    },
                    acars: {
                        enabled: view.plane.settings.routeFlown.acars.enabled,
                        color: view.plane.settings.routeFlown.acars.color
                    },
                    adsbTerrestrial: {
                        enabled: view.plane.settings.routeFlown.adsbTerrestrial.enabled,
                        color: view.plane.settings.routeFlown.adsbTerrestrial.color
                    },
                    adsbSatellite: {
                        enabled: view.plane.settings.routeFlown.adsbSatellite.enabled,
                        color: view.plane.settings.routeFlown.adsbSatellite.color
                    },
                    faaAnsp: {
                        enabled: view.plane.settings.routeFlown.faaAnsp.enabled,
                        color: view.plane.settings.routeFlown.faaAnsp.color
                    },
                },
                flown3PdiColor: view.plane.settings.flown3PdiColor ?? undefined,
                flownProjectionColor: view.plane.settings.flownProjectionColor ?? undefined,
                showFlightPlanPointMark: view.plane.settings.showFlightPlanPointMark,
                showFlightPlanPointLabel: view.plane.settings.showFlightPlanPointLabel,
                flightPlanExtent: view.plane.settings.flightPlanExtent,
                showRouteFlownPointMark: view.plane.settings.showRouteFlownPointMark,
                showFlightPlanTime: view.plane.settings.showFlightPlanTime,
                showProjectedPoints: view.plane.settings.showProjectedPoints,
                showLastReal: view.plane.settings.showLastReal,
                showProjectedHighlight: view.plane.settings.showProjectedHighlight,
                projectedHighlightColor: view.plane.settings.projectedHighlightColor ?? undefined,
                enableCoastMode: view.plane.settings.enableCoastMode,
                historyModern: view.plane.settings.historyModern,
                historyColorSource: view.plane.settings.historyColorSource === UDViewHistoryColorSource.aircraft ? 0 : 1,
                showCoastPosition: view.plane.settings.showCoastPosition,
                coastPositionColor: view.plane.settings.coastPositionColor ?? undefined,
                enableTimeDistance: view.plane.settings.enableTimeDistance,
                udViewTimeDistanceMode: view.plane.settings.udViewTimeDistanceMode,
                cityPair: {
                    show: view.plane.settings.cityPair.show,
                    active: view.plane.settings.cityPair.active,
                    planned: view.plane.settings.cityPair.planned,
                    scheduled: view.plane.settings.cityPair.scheduled,
                    activeColor: view.plane.settings.cityPair.activeColor,
                    plannedColor: view.plane.settings.cityPair.plannedColor,
                    scheduledColor: view.plane.settings.cityPair.scheduledColor
                }
            },
            filter: {
                useFilters: view.plane.filter.useFilters,
                startWithNoPlanes: view.plane.filter.startWithNoPlanes,
                planeFilterSettings: view.plane.filter.planeFilterSettings.map((filter) => filterToApi(filter))
            },
            icon: {
                zoomLevel: view.plane.icon.zoomLevel,
                size: view.plane.icon.size,
                type: view.plane.icon.type,
                drawShadow: view.plane.icon.drawShadow
            },
            tag: {
                show: view.plane.tag.show,
                labels: view.plane.tag.labels.map((label) => ({
                    type: label.type,
                    label: label.label ?? undefined,
                    enabled: label.enabled
                })),
                zoomLevel: view.plane.tag.zoomLevel,
                disableTagLeader: view.plane.tag.disableTagLeader,
                enableAutoTag: view.plane.tag.enableAutoTag,
                tagShowMyNotesIcon: view.plane.tag.tagShowMyNotesIcon,
                hideAirlineId: view.plane.tag.hideAirlineId,
                showLabelBackground: view.plane.tag.showLabelBackground,
                backgroundColor: view.plane.tag.backgroundColor ?? undefined,
                textColor: view.plane.tag.textColor ?? undefined,
                tagOutlineColor: view.plane.tag.outlineColor ?? undefined,
                tagOutlineMode: view.plane.tag.outlineMode
            },
            range: {
                isVisible: view.plane.range.isVisible,
                factor: view.plane.range.factor,
                type: view.plane.range.type,
                color: view.plane.range.color ?? undefined,
                lineColor: view.plane.range.lineColor ?? undefined
            },
            vector: {
                isVisible: view.plane.vector.isVisible,
                factor: view.plane.vector.factor,
                type: view.plane.vector.type,
                lineColor: view.plane.vector.lineColor ?? undefined
            }
        },
        weather: {
            show: view.weather.show,
            WeatherList: view.weather.all,
            hidden: view.weather.hidden,
            settings: view.weather.settings.map((item) => ({
                key: item.key,
                value: item.value
            }))
        },
        staticData: {
            airport: {
                show: view.staticData.airport.show,
                myAirports: view.staticData.airport.myAirports.show,
                myAirportsColor: view.staticData.airport.myAirports.color ?? undefined,
                tower: view.staticData.airport.tower.show,
                towerColor: view.staticData.airport.tower.color ?? undefined,
                noTower: view.staticData.airport.noTower.show,
                noTowerColor: view.staticData.airport.noTower.color ?? undefined,
                heliport: view.staticData.airport.heliport.show,
                heliportColor: view.staticData.airport.heliport.color ?? undefined,
                seaplane: view.staticData.airport.seaplane.show,
                seaplaneColor: view.staticData.airport.seaplane.color ?? undefined,
                others: view.staticData.airport.others.show,
                othersColor: view.staticData.airport.others.color ?? undefined,
                runways: view.staticData.airport.runways,
                deicing: view.staticData.airport.deicing,
                filterGroup: filterGroupToApi(view.staticData.airport.filterGroup),
                settings: airportNavSettingsToApi(view.staticData.airport.settings)
            },
            airspace: {
                show: view.staticData.airspace.show,
                fir: view.staticData.airspace.fir,
                uir: view.staticData.airspace.uir,
                classA: view.staticData.airspace.classA,
                classB: view.staticData.airspace.classB,
                classC: view.staticData.airspace.classC,
                classD: view.staticData.airspace.classD,
                classE: view.staticData.airspace.classE,
                classF: view.staticData.airspace.classF,
                classG: view.staticData.airspace.classG,
                other: view.staticData.airspace.other,
                filterGroup: filterGroupToApi(view.staticData.airspace.filterGroup)
            },
            airway: {
                show: view.staticData.airway.show,
                high: view.staticData.airway.high.show,
                low: view.staticData.airway.low.show,
                filterGroup: filterGroupToApi(view.staticData.airway.filterGroup),
                settings: {
                    fontSize: view.staticData.airway.settings.fontSize
                }
            },
            drawingObjects: {
                show: view.staticData.drawingObjects.show,
                avoidance: view.staticData.drawingObjects.avoidance,
                alert: view.staticData.drawingObjects.alert,
                view: view.staticData.drawingObjects.view,
                viewObjectIds: view.staticData.drawingObjects.viewObjectIds
            },
            fixes: {
                show: view.staticData.fixes.show,
                latLon: view.staticData.fixes.latLon.show,
                latLonColor: view.staticData.fixes.latLon.color ?? undefined,
                nrs: view.staticData.fixes.nrs.show,
                nrsColor: view.staticData.fixes.nrs.color ?? undefined,
                intersection: view.staticData.fixes.intersection.show,
                intersectionColor: view.staticData.fixes.intersection.color ?? undefined,
                rnav: view.staticData.fixes.rnav.show,
                rnavColor: view.staticData.fixes.rnav.color ?? undefined,
                terminal: view.staticData.fixes.terminal.show,
                terminalColor: view.staticData.fixes.terminal.color ?? undefined,
                vfr: view.staticData.fixes.vfr.show,
                vfrColor: view.staticData.fixes.vfr.color ?? undefined,
                other: view.staticData.fixes.other.show,
                otherColor: view.staticData.fixes.other.color ?? undefined,
                low: view.staticData.fixes.low,
                high: view.staticData.fixes.high,
                settings: {
                    fontSize: view.staticData.fixes.settings.fontSize
                },
                filterGroup: filterGroupToApi(view.staticData.fixes.filterGroup)
            },
            flightServiceStation: {
                show: view.staticData.flightServiceStation.show,
                filterGroup: filterGroupToApi(view.staticData.flightServiceStation.filterGroup)
            },
            geographyMap: {
                drawDayNight: view.staticData.geographyMap.drawDayNight
            },
            holding: {
                show: view.staticData.holding.show,
                lowAltitude: view.staticData.holding.lowAltitude,
                highAltitude: view.staticData.holding.highAltitude,
                sid: view.staticData.holding.sid,
                star: view.staticData.holding.star,
                approach: view.staticData.holding.approach,
                missedApproach: view.staticData.holding.missedApproach,
                other: view.staticData.holding.other,
                filterGroup: filterGroupToApi(view.staticData.holding.filterGroup)
            },
            gridMora: {
                show: view.staticData.gridMora.show
            },
            latLon: {
                show: view.staticData.latLon.show
            },
            navaids: {
                show: view.staticData.navaids.show,
                ndb: view.staticData.navaids.ndb.show,
                ndbColor: view.staticData.navaids.ndb.color ?? undefined,
                vor: view.staticData.navaids.vor.show,
                vorColor: view.staticData.navaids.vor.color ?? undefined,
                voR_DME: view.staticData.navaids.voR_DME.show,
                vorDmeColor: view.staticData.navaids.voR_DME.color ?? undefined,
                vortac: view.staticData.navaids.vortac.show,
                vortacColor: view.staticData.navaids.vortac.color ?? undefined,
                tacan: view.staticData.navaids.tacan.show,
                tacanColor: view.staticData.navaids.tacan.color ?? undefined,
                dme: view.staticData.navaids.dme.show,
                dmeColor: view.staticData.navaids.dme.color ?? undefined,
                ndB_DME: view.staticData.navaids.ndB_DME.show,
                ndbDmeColor: view.staticData.navaids.ndB_DME.color ?? undefined,
                rings: {
                    draw: view.staticData.navaids.rings.draw,
                    drawTerminal: view.staticData.navaids.rings.drawTerminal,
                    drawLow: view.staticData.navaids.rings.drawLowAltitude,
                    drawHigh: view.staticData.navaids.rings.drawHighAltitude,
                    drawUnrestricted: view.staticData.navaids.rings.drawUnrestricted,
                    fillColor: view.staticData.navaids.rings.fillColor,
                    lineColor: view.staticData.navaids.rings.lineColor
                },
                settings: {
                    fontSize: view.staticData.navaids.settings.fontSize
                },
                filterGroup: filterGroupToApi(view.staticData.navaids.filterGroup)
            },
            overwaterAreas: {
                show: view.staticData.overwaterAreas.show,
                lineColor: view.staticData.overwaterAreas.lineColor
            },
            miscellaneous: {
                backGroundBrightness: Math.round(view.staticData.miscellaneous.backGroundBrightness)
            },
            sids: {
                show: view.staticData.sids.show,
                filterGroup: filterGroupToApi(view.staticData.sids.filterGroup)
            },
            stars: {
                show: view.staticData.stars.show,
                filterGroup: filterGroupToApi(view.staticData.stars.filterGroup)
            },
            sua: {
                show: view.staticData.sua.show,
                static: view.staticData.sua.static,
                dynamic: view.staticData.sua.dynamic,
                alert: view.staticData.sua.alert,
                caution: view.staticData.sua.caution,
                danger: view.staticData.sua.danger,
                militaryArea: view.staticData.sua.militaryArea,
                prohibited: view.staticData.sua.prohibited,
                restricted: view.staticData.sua.restricted,
                traning: view.staticData.sua.traning,
                warning: view.staticData.sua.warning,
                unkown: view.staticData.sua.unkown,
                filterGroup: filterGroupToApi(view.staticData.sua.filterGroup)
            },
            timeZone: {
                show: view.staticData.timeZone.show
            },
            weatherStation: {
                show: view.staticData.weatherStation.show,
                filterGroup: filterGroupToApi(view.staticData.weatherStation.filterGroup)
            },
            customGeojson: {
                show: view.staticData.customGeojson.show,
                paths: view.staticData.customGeojson.paths.map((path) => {
                    if (path.type === 'url') {
                        return {
                            type: 0,
                            name: path.name,
                            show: path.show,
                            url: path.url
                        } satisfies UDViewGeojsonPathModel;
                    } else {
                        return {
                            type: 1,
                            name: path.name,
                            show: path.show,
                            fileHandle: path.fileHandle
                        } satisfies UDViewGeojsonPathModel;
                    }
                })
            }
        },
        map: {
            dayNightShading: view.map.dayNightShading,
            dayNightShadingType: view.map.dayNightShadingType,
            selectedTwilightTypes: view.map.selectedTwilightTypes,
            lat: view.map.lat,
            lon: view.map.lon,
            pitch: view.map.pitch,
            bearing: view.map.bearing,
            zoom: view.map.zoom,
            terrain: view.map.terrain,
            style: view.map.style,
            useGlobeView: view.map.useGlobeView,
            globeAutoRotate: view.map.globeAutoRotate,
            globeAutoRotateSpeed: view.map.globeAutoRotateSpeed,
            terrainShading: {
                enabled: view.map.terrainShading.enabled,
                type: view.map.terrainShading.type,
                opacity: view.map.terrainShading.opacity,
                altitude: view.map.terrainShading.altitude,
                color: view.map.terrainShading.color
            }
        }
    };
}

function apiFilterGroupToFilterGroup(api: ApiUDViewFilterGroupModel | undefined): UDViewFilterGroupModel {
    return {
        show: api?.show ?? false,
        ids: api?.filterGroupIds ?? [],
        config: api?.config ?? {}
    };
}

function filterGroupToApi(group: UDViewFilterGroupModel): ApiUDViewFilterGroupModel {
    return {
        show: group.show,
        config: group.config,
        filterGroupIds: group.ids
    };
}
